import { FetchResult, MutationFunctionOptions, MutationHookOptions, MutationResult, useMutation } from '@apollo/client';
import { batteryForecastDataQuery, forecastDataQuery } from '@e2m-customer-portal/hooks';
import {
  MutationRevokeLegacyDowntimeArgs,
  MutationSetLegacyDowntimeArgs,
  RevokeDowntimeMutation,
  RevokeDowntimeMutationVariables,
  RevokeLegacyDowntimeMutation,
  SetDowntimeMutation,
  SetDowntimeMutationVariables,
  SetLegacyDowntimeMutation,
} from '../../generated/graphql';
import { clearCache } from '../../utils';
import {
  revokeDowntimeMutation,
  revokeLegacyDowntimeMutation,
  setDowntimeMutation,
  setLegacyDowntimeMutation,
} from './query-definitions';

/**
 * Mutation to signal that a device of a marketLocation is offline for a time because of a
 * specified reason.
 */
export const useSetLegacyDowntimeMutation = (
  options?: MutationHookOptions<SetLegacyDowntimeMutation, MutationSetLegacyDowntimeArgs>,
): [
  (
    marketLocationId: string,
    deviceNumber: string,
    from: Date,
    to: Date,
    opts?: MutationFunctionOptions<SetLegacyDowntimeMutation, MutationSetLegacyDowntimeArgs>,
  ) => Promise<FetchResult<SetLegacyDowntimeMutation>>,
  MutationResult<SetLegacyDowntimeMutation>,
] => {
  const [update, response] = useMutation<SetLegacyDowntimeMutation, MutationSetLegacyDowntimeArgs>(
    setLegacyDowntimeMutation,
    options,
  );

  return [
    (
      marketLocationId,
      deviceNumber,
      from,
      to,
      opts?: MutationFunctionOptions<SetLegacyDowntimeMutation, MutationSetLegacyDowntimeArgs>,
    ) =>
      update({
        ...opts,
        variables: {
          marketLocationId,
          deviceNumber,
          from,
          to,
        },
        update: clearCache(forecastDataQuery),
      }),
    response,
  ];
};

export const useSetDowntimeMutation = (
  options?: MutationHookOptions<SetDowntimeMutation, SetDowntimeMutationVariables>,
): [
  (
    deviceNumber: string,
    from: Date,
    to: Date,
    opts?: MutationFunctionOptions<SetDowntimeMutation, SetDowntimeMutationVariables>,
  ) => Promise<FetchResult<SetDowntimeMutation>>,
  MutationResult<SetDowntimeMutation>,
] => {
  const [update, response] = useMutation<SetDowntimeMutation, SetDowntimeMutationVariables>(
    setDowntimeMutation,
    options,
  );

  return [
    (deviceNumber, from, to, opts?) =>
      update({
        ...opts,
        variables: {
          deviceNumber,
          from,
          to,
        },
        update: clearCache(forecastDataQuery, batteryForecastDataQuery),
      }),
    response,
  ];
};

export const useRevokeLegacyDowntimeMutation = (
  options?: MutationHookOptions<RevokeLegacyDowntimeMutation, MutationRevokeLegacyDowntimeArgs>,
): [
  (
    deviceNumber: string,
    from: Date,
    to: Date,
    options?: MutationHookOptions<RevokeLegacyDowntimeMutation, MutationRevokeLegacyDowntimeArgs>,
  ) => Promise<FetchResult<RevokeLegacyDowntimeMutation>>,
  MutationResult<RevokeLegacyDowntimeMutation>,
] => {
  const [update, response] = useMutation<RevokeLegacyDowntimeMutation, MutationRevokeLegacyDowntimeArgs>(
    revokeLegacyDowntimeMutation,
    options,
  );

  return [
    (deviceNumber, from, to, options?) =>
      update({
        ...options,
        variables: {
          deviceNumber,
          from,
          to,
        },
        update: clearCache(forecastDataQuery),
      }),
    response,
  ];
};

export const useRevokeDowntimeMutation = (
  options?: MutationHookOptions<RevokeDowntimeMutation, RevokeDowntimeMutationVariables>,
): [
  (
    deviceNumber: string,
    from: Date,
    to: Date,
    opts?: MutationFunctionOptions<RevokeDowntimeMutation, RevokeDowntimeMutationVariables>,
  ) => Promise<FetchResult<RevokeDowntimeMutation>>,
  MutationResult<RevokeDowntimeMutation>,
] => {
  const [update, response] = useMutation<RevokeDowntimeMutation, RevokeDowntimeMutationVariables>(
    revokeDowntimeMutation,
    options,
  );

  return [
    (deviceNumber, from, to, opts?) =>
      update({
        ...opts,
        variables: {
          deviceNumber,
          from,
          to,
        },
        update: clearCache(forecastDataQuery, batteryForecastDataQuery),
      }),
    response,
  ];
};
